import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import portalClient from './portalClient';
import PortalLayout from './PortalLayout';
import { Button, Card, Input, Modal, Select, Skeleton, useToast } from '../design-system';
import './Portal.css';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null;

// Client-facing status wording. The internal enum (open/in_progress/fulfilled/
// closed) is written for staff; a client wants to know whose court the ball is in.
const STATUS_TEXT = {
  open: 'Open',
  in_progress: 'In progress',
  fulfilled: 'Done',
  closed: 'Closed',
};

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'fulfilled', label: 'Done' },
];

function PortalRequests() {
  const navigate = useNavigate();
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [askOpen, setAskOpen] = useState(false);
  const [draft, setDraft] = useState({ title: '', description: '', priority: 'medium' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = filter === 'all' ? {} : { status: filter };
      const res = await portalClient.get('/api/portal/requests', { params });
      setRequests(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not load your requests');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const submitAsk = async () => {
    if (!draft.title.trim()) {
      toast.danger('Please say what you need');
      return;
    }
    setSaving(true);
    try {
      const res = await portalClient.post('/api/portal/requests', draft);
      setAskOpen(false);
      setDraft({ title: '', description: '', priority: 'medium' });
      toast.success('Sent — your accountant has been notified');
      navigate(`/portal/requests/${res.data.id}`);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not send your request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PortalLayout
      title="Requests"
      subtitle="Documents and information your accountant needs — and anything you want to ask them."
    >
      <div className="portal-toolbar">
        <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="portal-filter">
          {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </Select>
        <Button variant="primary" onClick={() => setAskOpen(true)}>Ask your accountant</Button>
      </div>

      {loading ? (
        <Card><Skeleton.Text lines={4} /></Card>
      ) : requests.length === 0 ? (
        <Card padding="lg">
          <div className="portal-empty">
            <div className="portal-empty-icon">📭</div>
            <p className="portal-empty-title">Nothing here yet</p>
            <p className="portal-muted">
              When your accountant needs something from you, it will appear here.
            </p>
          </div>
        </Card>
      ) : (
        <div className="portal-request-list">
          {requests.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`portal-request-row${r.hasUnread ? ' is-unread' : ''}`}
              onClick={() => navigate(`/portal/requests/${r.id}`)}
            >
              <div className="portal-request-main">
                <span className="portal-request-title">
                  {r.title}
                  {r.hasUnread && <span className="portal-dot" title="New reply" />}
                </span>
                <span className="portal-request-meta">
                  {/* Who started it matters to a client: one is a to-do, the
                      other is their own question awaiting an answer. */}
                  {r.raisedByClient ? 'You asked' : 'Requested by your accountant'}
                  {r.dueDate ? ` · needed by ${fmtDate(r.dueDate)}` : ''}
                  {r.commentCount > 0 ? ` · ${r.commentCount} message${r.commentCount === 1 ? '' : 's'}` : ''}
                </span>
              </div>
              <span className={`portal-status portal-status-${r.status}`}>
                {STATUS_TEXT[r.status] || r.status}
              </span>
            </button>
          ))}
        </div>
      )}

      <Modal
        isOpen={askOpen}
        onClose={() => setAskOpen(false)}
        title="Ask your accountant"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAskOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={submitAsk} loading={saving}>Send</Button>
          </>
        }
      >
        <Input
          label="What do you need?"
          autoFocus
          placeholder="e.g. A CA certificate for a bank loan"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
        <div className="ds-field">
          <label className="ds-field-label" htmlFor="portal-ask-desc">Any details</label>
          <textarea
            id="portal-ask-desc"
            className="ds-input"
            rows="4"
            placeholder="Dates, amounts, who it's for…"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </div>
        <Select
          label="How urgent is it?"
          value={draft.priority}
          onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
        >
          <option value="medium">Normal</option>
          <option value="high">Urgent</option>
        </Select>
      </Modal>
    </PortalLayout>
  );
}

export default PortalRequests;
